import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { VIEWS } from "common/components/CalendarPro";

export enum CalendarFontSizes {
  SMALL = "calFontSizeSmall",
  NORMAL = "calFontSizeNormal",
  LARGE = "calFontSizeLarge",
}

export type CalendarSettings = {
  view: VIEWS;
  show_indicators: boolean;
  show_full_events: boolean;
  show_closed_events: boolean;
  show_all_programs: boolean;
  always_show_remaining_spaces: boolean;
  font_size: CalendarFontSizes;
  compact_filters: boolean;
};
export type CateringSettings = {
  boatIds: number[];
  programIds: number[];
  detailsOpen?: boolean;
  show_all_programs: boolean;
};
export type HostSettings = {
  boatIds: number[];
  programIds: number[];
  detailsOpen?: boolean;
  show_all_programs: boolean;
};
export type AdvancedReportSettings = {
  boatIds: number[];
  programIds: number[];
  show_all_programs: boolean;
  show_filter: boolean;
  show_canceled: boolean;
  short_prices: boolean;
  chartType: "line" | "bar" | "pie";
  currency?: null | "HUF" | "USD";
  value:
    | "amount"
    | "count"
    | "guests"
    | "refund_amount"
    | "admin_fee_amount"
    | "refund_guests"
    | "canceled_amount"
    | "canceled_count"
    | "canceled_guests";
  groupBy: "program" | "program_variant" | "boat" | "partner" | "partner_group";
  dateType: "travel" | "booking";
};
export type SalesReportSettings = {
  boatIds: number[];
  programIds: number[];
  show_all_programs: boolean;
  show_filter: boolean;
  show_canceled: boolean;
  short_prices: boolean;
  chartType: "line" | "bar" | "pie";
  currency?: null | "HUF" | "USD";
  value:
    | "amount"
    | "count"
    | "guests"
    | "refund_amount"
    | "admin_fee_amount"
    | "refund_guests"
    | "canceled_amount"
    | "canceled_count"
    | "canceled_guests";
  groupBy: "program" | "program_variant" | "boat" | "partner" | "partner_group";
  dateType: "travel" | "booking";
};
export type SimpleReportSettings = {
  boatIds: number[];
  programIds: number[];
  show_all_programs: boolean;
  show_filter: boolean;
  show_canceled: boolean;
  short_prices: boolean;
  hide_tip: boolean;
  chartType: "line" | "bar" | "pie";
  currency?: null | "HUF" | "USD";
  value:
    | "amount"
    | "count"
    | "guests"
    | "refund_amount"
    | "admin_fee_amount"
    | "refund_guests"
    | "canceled_amount"
    | "canceled_count"
    | "canceled_guests";
  groupBy: "program" | "program_variant" | "boat" | "partner" | "partner_group";
  dateType: "travel" | "booking";
};
export type StreetSalesSettings = {
  boatIds: number[];
  programIds: number[];
  show_all_programs: boolean;
};
export type KitchenSettings = {
  boatIds: number[];
  programIds: number[];
  show_all_programs: boolean;
};
export type SettingsState = {
  calendar: CalendarSettings;
  catering: CateringSettings;
  host: HostSettings;
  streetSales: StreetSalesSettings;
  kitchen: KitchenSettings;
  advancedReport: AdvancedReportSettings;
  salesReport: SalesReportSettings;
  simpleReport: SimpleReportSettings;
  // Add more settings keys here as needed
};
const initialState: SettingsState = {
  calendar: {
    view: VIEWS.WEEK,
    show_closed_events: true,
    show_full_events: true,
    show_indicators: true,
    show_all_programs: false,
    always_show_remaining_spaces: false,
    font_size: CalendarFontSizes.SMALL,
    compact_filters: false,
  },
  catering: {
    boatIds: [],
    programIds: [],
    show_all_programs: false,
  },
  host: {
    boatIds: [],
    programIds: [],
    show_all_programs: false,
  },
  kitchen: {
    boatIds: [],
    programIds: [],
    show_all_programs: false,
  },
  streetSales: {
    boatIds: [],
    programIds: [],
    show_all_programs: false,
  },
  advancedReport: {
    boatIds: [],
    programIds: [],
    show_all_programs: false,
    show_filter: false,
    chartType: "bar",
    value: "amount",
    groupBy: "program",
    show_canceled: true,
    short_prices: true,
    dateType: "travel",
  },
  salesReport: {
    boatIds: [],
    programIds: [],
    show_all_programs: false,
    show_filter: false,
    chartType: "bar",
    value: "amount",
    groupBy: "program",
    show_canceled: true,
    short_prices: true,
    dateType: "travel",
  },
  simpleReport: {
    boatIds: [],
    programIds: [],
    show_all_programs: false,
    show_filter: false,
    chartType: "bar",
    value: "guests",
    groupBy: "program",
    show_canceled: true,
    short_prices: true,
    hide_tip: false,
    dateType: "travel",
  },
};

export const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    updateSettings: (
      state,
      action: PayloadAction<{ key: keyof SettingsState; settings: any }>
    ) => {
      state[action.payload.key] = action.payload.settings;
    },
  },
});

export const { updateSettings } = settingsSlice.actions;

export const selectSettingsByKey = (
  state: RootState,
  key: keyof SettingsState
) => state.settings[key];

export default settingsSlice.reducer;
